import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2, RefreshCw, Send, Shield, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import TicketStatusBadge from './TicketStatusBadge';
import SupportMessageEditor from './SupportMessageEditor';
import SupportMessageRenderer from './SupportMessageRenderer';
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  categoryLabel,
  formatTicketDate,
  priorityLabel,
  stringifySupportBody,
  isSupportBodyEmpty,
} from '../../lib/supportConstants';
import { cn } from '../../lib/utils';

function MessageBubble({ message }) {
  const isAdmin = message.author_role === 'admin';
  const isInternal = message.is_internal;

  return (
    <div
      className={`flex gap-3 ${isAdmin ? 'flex-row' : 'flex-row-reverse'} ${isInternal ? 'opacity-90' : ''}`}
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-medium ${
          isAdmin
            ? 'bg-violet-600 text-white'
            : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
        }`}
      >
        {isAdmin ? <Shield className="w-4 h-4" /> : <User className="w-4 h-4" />}
      </div>
      <div className={`min-w-0 flex-1 max-w-[85%] ${isAdmin ? '' : 'text-right'}`}>
        <div className={`flex flex-wrap items-baseline gap-2 mb-1 ${isAdmin ? '' : 'justify-end'}`}>
          <span className="text-sm font-medium text-slate-900 dark:text-white">
            {message.author_name || message.author_email || (isAdmin ? 'Support' : 'You')}
          </span>
          <span className="text-[11px] text-slate-400">{formatTicketDate(message.created_at)}</span>
          {isInternal ? (
            <span className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400 font-medium">
              Internal note
            </span>
          ) : null}
        </div>
        <div
          className={`rounded-2xl px-4 py-3 text-left ${
            isInternal
              ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800'
              : isAdmin
                ? 'bg-violet-50 dark:bg-violet-950/30 border border-violet-100 dark:border-violet-900'
                : 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
          }`}
        >
          <SupportMessageRenderer body={message.body} />
        </div>
      </div>
    </div>
  );
}

export default function TicketDetailPanel({
  ticket,
  mode = 'client',
  orgId,
  loading = false,
  submitting = false,
  refreshing = false,
  onReply,
  onRefresh,
  onUpdateMeta,
}) {
  const editorRef = useRef(null);
  const [internalNote, setInternalNote] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  useEffect(() => {
    setComposerOpen(false);
    setInternalNote(false);
  }, [ticket?.id]);

  const openComposer = () => setComposerOpen(true);

  const closeComposer = async () => {
    if (editorRef.current) {
      const empty = await editorRef.current.isEmpty();
      if (empty) setComposerOpen(false);
    } else {
      setComposerOpen(false);
    }
  };

  const focusEditor = () => {
    requestAnimationFrame(() => editorRef.current?.focus());
  };

  const handleSend = async () => {
    if (!editorRef.current || !onReply) return;
    const data = await editorRef.current.save();
    if (isSupportBodyEmpty(data)) return;
    await onReply(stringifySupportBody(data), mode === 'admin' ? internalNote : false);
    await editorRef.current.clear();
    setInternalNote(false);
    setComposerOpen(false);
  };

  const closed = ticket?.status === 'closed';

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 text-slate-500 py-20 h-full">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading ticket…
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center text-center px-8 py-20 text-slate-500 h-full">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Select a ticket</p>
        <p className="text-xs mt-1 max-w-xs">Pick a conversation from the list or create a new request.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-white dark:bg-slate-900">
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-800 px-4 py-3 space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-mono text-slate-500">#{ticket.ticket_number}</p>
            <h2 className="font-[Manrope] text-lg font-semibold text-slate-900 dark:text-white mt-0.5 truncate">
              {ticket.subject}
            </h2>
            {mode === 'admin' && ticket.organization_name ? (
              <p className="text-xs text-slate-500 mt-0.5 truncate">{ticket.organization_name}</p>
            ) : null}
            <p className="text-xs text-slate-500 mt-0.5">
              {ticket.created_by_name || ticket.created_by_email} · {formatTicketDate(ticket.created_at)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={onRefresh}
              disabled={!onRefresh || refreshing}
              title="Refresh conversation"
            >
              <RefreshCw className={cn('w-3.5 h-3.5 mr-1.5', refreshing && 'animate-spin')} />
              Refresh
            </Button>
            <TicketStatusBadge status={ticket.status} />
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
              {categoryLabel(ticket.category)}
            </span>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
              {priorityLabel(ticket.priority)}
            </span>
          </div>
        </div>

        {mode === 'admin' && onUpdateMeta ? (
          <div className="flex flex-wrap gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <select
                className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                value={ticket.status}
                onChange={(e) => onUpdateMeta({ status: e.target.value })}
              >
                {TICKET_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Priority</Label>
              <select
                className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                value={ticket.priority}
                onChange={(e) => onUpdateMeta({ priority: e.target.value })}
              >
                {TICKET_PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <select
                className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                value={ticket.category}
                onChange={(e) => onUpdateMeta({ category: e.target.value })}
              >
                {TICKET_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        {mode === 'client' && !closed && ticket.status === 'waiting_customer' ? (
          <p className="text-xs text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-950/30 rounded-md px-3 py-2">
            Support replied — please respond below when you can.
          </p>
        ) : null}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-6 bg-slate-50/50 dark:bg-slate-950/30">
        {(ticket.messages || []).map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
      </div>

      {!closed ? (
        <div className="shrink-0 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-[0_-4px_24px_-8px_rgba(0,0,0,0.08)]">
          {!composerOpen ? (
            <button
              type="button"
              onClick={openComposer}
              className="w-full text-left px-4 py-3.5 text-sm text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
            >
              Write your reply…
            </button>
          ) : (
            <div className="flex flex-col max-h-[min(52vh,420px)]">
              {mode === 'admin' ? (
                <div className="shrink-0 flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-2">
                    <Switch id="internal-note" checked={internalNote} onCheckedChange={setInternalNote} />
                    <Label htmlFor="internal-note" className="text-xs text-slate-600 cursor-pointer">
                      Internal note
                    </Label>
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={closeComposer}>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Minimize
                  </Button>
                </div>
              ) : (
                <div className="shrink-0 flex justify-end px-4 py-1.5 border-b border-slate-100 dark:border-slate-800">
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={closeComposer}>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Minimize
                  </Button>
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-y-auto border-b border-slate-100 dark:border-slate-800">
                <SupportMessageEditor
                  key={`composer-${ticket.id}`}
                  ref={editorRef}
                  orgId={orgId || ticket?.organization_id}
                  placeholder="Write your reply…"
                  expanded
                  onReady={focusEditor}
                />
              </div>
              <div className="shrink-0 flex justify-end gap-2 px-4 py-3">
                <Button type="button" variant="outline" size="sm" onClick={() => setComposerOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSend} disabled={submitting} size="sm">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  {internalNote ? 'Add note' : 'Send reply'}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="shrink-0 border-t border-slate-200 dark:border-slate-800 px-4 py-3 text-center text-xs text-slate-500 bg-slate-50 dark:bg-slate-900/80">
          This ticket is closed. Open a new ticket if you need more help.
        </div>
      )}
    </div>
  );
}
